import { ImageProps } from 'next/image';

import { Article } from '@natu/ui/Article';
import { cn } from '@natu/utils/cn';
import { getSlugWithDash } from '@natu/utils/getSlugWithDash';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { getAssetFromStoryblok, SbComponentType, sbEditable } from '../../../../../../utils';
import { ArticleNodes } from '../../../../../../utils/src/types/Blog/ArticleNode.types';
import { getDateAndReadTime } from '../../../../utils/getDateAndReadTime';

interface RecommendedArticlesProps {
  recommendedArticles?: ArticleNodes['stories'];
}

export const RecommendedArticles = ({ recommendedArticles }: RecommendedArticlesProps) => {
  const gridStyles = cn(
    'grid gap-8 lg:gap-y-10',
    recommendedArticles && recommendedArticles.length === 1 && 'lg:lg:grid-cols-1',
    recommendedArticles && recommendedArticles.length === 2 && 'lg:lg:grid-cols-2',
    recommendedArticles && recommendedArticles.length === 3 && 'lg:lg:grid-cols-3',
    recommendedArticles && recommendedArticles?.length > 3 && 'lg:grid-cols-4',
  );

  if (!isArrayWithLength(recommendedArticles)) return null;

  return (
    <div className="bg-naturaily-azureGray flex flex-col gap-y-8 rounded-3xl px-6 py-12 lg:p-10 lg:pb-12">
      <p className="line-[2.25rem] text-[1.75rem] font-semibold tracking-[-0.3px] lg:grid-cols-1">
        Recommended articles
      </p>
      <ul className={cn(gridStyles, '[&>li>div>a]:max-h-72 [&>li>div>a]:w-full')}>
        {recommendedArticles.map((article, _i) => {
          const { _uid, title, description, timeToRead, previewImage } = article.content || {};
          const restProps = sbEditable(article.content as SbComponentType);
          const image = getAssetFromStoryblok(previewImage) as ImageProps;

          return (
            <li key={`${_uid}${_i}`} {...restProps}>
              <Article
                label={getDateAndReadTime(article.first_published_at, timeToRead)}
                title={title || ''}
                description={description}
                image={image}
                url={getSlugWithDash(article.full_slug)}
                showButton={false}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
